/**
 * VidchatRoom.tsx — layout:
 *   left column  : video grid (2x2, top) + chat log & input (bottom)
 *   right column : you + room/user list with per-user watch (👁) / mute (🔊) toggles
 * All see/hear toggles are local to the viewer.
 */
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useVidchat } from '../hooks/useVidchat.js';
import { rankBadge, rankLabel, isAdminK, isModK } from '../lib/ranks.js';
import { DISCLAIMER_TITLE, DISCLAIMER_INTRO, DISCLAIMER_SECTIONS, DISCLAIMER_AGREE_LABEL } from '../lib/disclaimer.js';
import { VideoGrid } from './VideoGrid.js';

const BAN_DURATIONS: Array<{ label: string; minutes: number }> = [
    { label: '10 min', minutes: 10 },
    { label: '1 hour', minutes: 60 },
    { label: '1 day', minutes: 1440 },
    { label: 'Permanent', minutes: 0 },
];

type Theme = 'modern' | 'classic' | 'oldchat';
const THEME_CYCLE: Theme[] = ['modern', 'classic', 'oldchat'];
const THEME_LABEL: Record<Theme, string> = { modern: 'Modern', classic: 'Classic', oldchat: 'OldChat' };

const WEBCODECS_OK = typeof window !== 'undefined'
    && 'VideoEncoder' in window && 'VideoDecoder' in window && 'MediaStreamTrackProcessor' in window;

// rank /showme vocab for the query: prefix matches first, then substring matches
function rankVocab(vocab: string[], q: string): string[] {
    if (!q) return vocab;
    const pre: string[] = [], sub: string[] = [];
    for (const w of vocab) {
        const i = w.indexOf(q);
        if (i === 0) pre.push(w);
        else if (i > 0) sub.push(w);
    }
    return [...pre, ...sub];
}

export function VidchatRoom() {
    const {
        conn, me, maxSubs, showmeVocab, rooms, currentRoom, currentTopic, joinError, banList, banned, disclaimerRequired, roster, streams, messages,
        publishing, sharingVideo, localStream, activeSubs, recordingStreams, notice, streamStats, userStats, starred,
        devices, audioInId, videoInId, listDevices, chooseAudioIn, chooseVideoIn, acceptDisclaimer,
        sendChat, joinRoom, createRoom, setRoomTopic, modKick, modBan, modUnban, modListBans,
        startCam, startMic, stopCam, subscribe, unsubscribe, toggleMute, attachCanvas, toggleRecord, dismissNotice,
    } = useVidchat();

    const [agreeChecked, setAgreeChecked] = useState(false);
    const [declined, setDeclined] = useState(false);

    const [draft, setDraft] = useState('');
    const [focused, setFocused] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [hl, setHl] = useState(0);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const msgEndRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

    // room switcher / party-room modals
    const [pwModal, setPwModal] = useState<{ room: string } | null>(null);
    const [pwInput, setPwInput] = useState('');
    const [pwError, setPwError] = useState(false);
    const [newModal, setNewModal] = useState(false);
    const [newName, setNewName] = useState('');
    const [newPw, setNewPw] = useState('');
    const [settingsOpen, setSettingsOpen] = useState(false);
    const openSettings = () => { void listDevices(); setSettingsOpen(true); };

    // moderation + topic
    const [modTarget, setModTarget] = useState<typeof roster[number] | null>(null);
    const [modReason, setModReason] = useState('');
    const [bansOpen, setBansOpen] = useState(false);
    const [editingTopic, setEditingTopic] = useState(false);
    const [topicDraft, setTopicDraft] = useState('');

    const isStaff = !!me && (me.is_mod || me.is_admin);
    const curRoomDir = rooms.find((r) => r.name === currentRoom);
    const isOwner = !!me && !!curRoomDir && !curRoomDir.isLobby && curRoomDir.owner === me.name;
    const canEditTopic = isStaff || isOwner;
    const canModerate = (u: typeof roster[number]) =>
        isStaff && u.id > 0 && u.id !== me?.id && !isAdminK(u.k) && (!!me?.is_admin || !isModK(u.k));

    const openBans = () => { modListBans(); setBansOpen(true); };
    // a successful switch (currentRoom changes) closes any open modal
    useEffect(() => { setPwModal(null); setNewModal(false); }, [currentRoom]);
    // a rejected password re-flags the prompt
    useEffect(() => {
        if (joinError && pwModal && joinError.room === pwModal.room) setPwError(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [joinError]);

    // theme cycle: Modern → Classic → OldChat, persisted locally
    const [theme, setTheme] = useState<Theme>(() => {
        try { const t = localStorage.getItem('vidchat-theme'); return t === 'classic' || t === 'oldchat' ? t : 'modern'; } catch { return 'modern'; }
    });
    useEffect(() => { try { localStorage.setItem('vidchat-theme', theme); } catch { /* ignore */ } }, [theme]);
    const cycleTheme = () => setTheme((t) => THEME_CYCLE[(THEME_CYCLE.indexOf(t) + 1) % THEME_CYCLE.length]!);

    const onRoomClick = (r: typeof rooms[number]) => {
        if (r.name === currentRoom) return;
        if (r.hasPassword) { setPwModal({ room: r.name }); setPwInput(''); setPwError(false); }
        else joinRoom(r.name);
    };

    // /showme autocomplete: show the detectable-object palette as the user types `/showme …`
    const showmeMatch = draft.match(/^\/showme(?:\s+(\S*))?$/i);
    const query = showmeMatch ? (showmeMatch[1] ?? '').toLowerCase() : null;
    const suggestions = (query !== null && focused && !dismissed) ? rankVocab(showmeVocab, query).slice(0, 8) : [];
    const hi = suggestions.length ? Math.min(hl, suggestions.length - 1) : 0;

    const acceptShowme = (word: string) => {
        setDraft(`/showme ${word}`);
        setDismissed(true);
        setHl(0);
        inputRef.current?.focus();
    };

    const onChatKey = (e: KeyboardEvent<HTMLInputElement>) => {
        if (suggestions.length === 0) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); setHl((h) => (h + 1) % suggestions.length); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setHl((h) => (h - 1 + suggestions.length) % suggestions.length); }
        else if (e.key === 'Escape') { e.preventDefault(); setDismissed(true); }
        else if (e.key === 'Tab') { e.preventDefault(); acceptShowme(suggestions[hi]!); }
        else if (e.key === 'Enter') {
            const cur = suggestions[hi];
            if (cur && query !== null && query !== cur) { e.preventDefault(); acceptShowme(cur); }
            // else fall through → form submits (sends the message)
        }
    };

    const watched = new Map(activeSubs.map((a) => [a.streamId, a]));
    const isGuest = !me || me.id === 0;
    const streamByUid = new Map<number, typeof streams[number]>();
    for (const s of streams) streamByUid.set(s.uid, s);

    return (
        <div className={`vc-root ${theme === 'classic' ? 'vc-classic' : theme === 'oldchat' ? 'vc-oldchat' : ''}`}>
            <header className="vc-header">
                <div className="vc-brand">
                    <img className="vc-logo-icon" src="/vidchat/app/legacy/ffr-icon.png" alt="FFR" />
                    <img className="vc-logo-word" src="/vidchat/app/legacy/ffr-wordmark.png" alt="FlashFlashRevolution" />
                </div>
                <div className="vc-header-right">
                    {WEBCODECS_OK && !isGuest && (
                        publishing
                            ? <button className="vc-btn vc-btn-stop" onClick={stopCam}>{sharingVideo ? 'Stop camera' : 'Stop mic'}</button>
                            : <>
                                <button className="vc-btn vc-btn-go" onClick={startCam}>Share camera</button>
                                <button className="vc-btn vc-btn-mic" onClick={startMic} title="share audio only (no camera)">Share mic</button>
                              </>
                    )}
                    {WEBCODECS_OK && !isGuest && (
                        <button className="vc-gear" onClick={openSettings} title="mic / camera settings">⚙</button>
                    )}
                    <button
                        className="vc-theme-toggle"
                        onClick={cycleTheme}
                        title="switch theme — Modern / Classic / OldChat"
                    >{THEME_LABEL[theme]}</button>
                    <span className={`vc-conn vc-conn-${conn}`}>{conn}</span>
                </div>
            </header>

            {!WEBCODECS_OK && (
                <div className="vc-warn">
                    Your browser doesn't support the WebCodecs APIs this video chat needs.
                    Try a recent Chrome, Edge, or Safari.
                </div>
            )}
            {notice && (
                <div className="vc-notice">{notice}<button className="vc-notice-x" onClick={dismissNotice}>✕</button></div>
            )}

            {(currentTopic || canEditTopic) && (
                <div className="vc-topicbar">
                    <span className="vc-topic-icon">📌</span>
                    {editingTopic ? (
                        <form className="vc-topic-form" onSubmit={(e) => { e.preventDefault(); setRoomTopic(topicDraft.trim()); setEditingTopic(false); }}>
                            <input className="vc-topic-input" autoFocus value={topicDraft} maxLength={200}
                                onChange={(e) => setTopicDraft(e.target.value)} placeholder="Set a room topic…" />
                            <button className="vc-topic-save vc-btn vc-btn-go" type="submit">Save</button>
                            <button className="vc-topic-cancel vc-btn vc-btn-ghost" type="button" onClick={() => setEditingTopic(false)}>Cancel</button>
                        </form>
                    ) : (
                        <>
                            <span className="vc-topic-text">{currentTopic || (canEditTopic ? 'Set a topic for this room…' : '')}</span>
                            {canEditTopic && <button className="vc-topic-edit" title="edit topic" onClick={() => { setTopicDraft(currentTopic); setEditingTopic(true); }}>✎</button>}
                        </>
                    )}
                </div>
            )}

            <div className="vc-body">
                {/* LEFT: video (top) + chat (bottom) */}
                <div className="vc-left">
                    <VideoGrid
                        localStream={localStream}
                        activeSubs={activeSubs}
                        streamStats={streamStats}
                        userStats={userStats}
                        starred={starred}
                        attachCanvas={attachCanvas}
                        unsubscribe={unsubscribe}
                        toggleMute={toggleMute}
                    />
                    <div className="vc-chat">
                        <div className="vc-chat-log">
                            {messages.map((m) => m.kind === 'whisper' ? (
                                <div key={m.id} className="vc-chat-line vc-whisper">
                                    {m.dir === 'out'
                                        ? <>→ you whisper to <b style={{ color: m.color }}>{m.to}</b>: {m.body}</>
                                        : <><b style={{ color: m.color }}>{m.name}</b> whispers: {m.body}</>}
                                </div>
                            ) : (
                                <div key={m.id} className="vc-chat-line">
                                    <b style={{ color: m.color }}>{m.name}</b>: {m.body}
                                </div>
                            ))}
                            <div ref={msgEndRef} />
                        </div>
                        <div className="vc-chat-entry">
                            {suggestions.length > 0 && (
                                <div className="vc-showme-pop">
                                    <div className="vc-showme-cap">show me… (pick one)</div>
                                    {suggestions.map((w, i) => (
                                        <div
                                            key={w}
                                            className={`vc-showme-item ${i === hi ? 'active' : ''}`}
                                            onMouseDown={(e) => { e.preventDefault(); acceptShowme(w); }}
                                            onMouseEnter={() => setHl(i)}
                                        >{w}</div>
                                    ))}
                                </div>
                            )}
                            <form
                                className="vc-chat-form"
                                onSubmit={(e) => { e.preventDefault(); if (!isGuest && draft.trim()) { sendChat(draft); setDraft(''); setDismissed(false); } }}
                            >
                                <input
                                    ref={inputRef}
                                    className="vc-chat-input"
                                    value={draft}
                                    onChange={(e) => { setDraft(e.target.value); setDismissed(false); setHl(0); }}
                                    onKeyDown={onChatKey}
                                    onFocus={() => setFocused(true)}
                                    onBlur={() => setFocused(false)}
                                    placeholder={isGuest ? 'Log in to chat' : 'Say something…  (try /showme cup)'}
                                    disabled={isGuest}
                                    maxLength={500}
                                />
                                <button className="vc-chat-send" disabled={isGuest || !draft.trim()}>Send</button>
                            </form>
                        </div>
                    </div>
                </div>

                {/* RIGHT: you + room/user list */}
                <aside className="vc-right">
                    <div className="vc-me">
                        {me
                            ? <>You are <b style={{ color: me.color }}>{me.name}</b>{isGuest ? ' (guest — watch only)' : <span className="vc-rank-label"> · {rankLabel(me.k)}</span>}</>
                            : 'connecting…'}
                    </div>

                    <div className="vc-rooms">
                        <div className="vc-rooms-head">
                            <span className="vc-roster-title">Rooms ({rooms.length})</span>
                            {!isGuest && (
                                <button
                                    className="vc-room-new"
                                    onClick={() => { setNewName(''); setNewPw(''); setNewModal(true); }}
                                    title="create a party room"
                                >+ New</button>
                            )}
                        </div>
                        <ul className="vc-room-list">
                            {rooms.map((r) => (
                                <li
                                    key={r.name}
                                    className={`vc-room-row ${r.name === currentRoom ? 'active' : ''}`}
                                    onClick={() => onRoomClick(r)}
                                    title={r.name === currentRoom ? 'current room'
                                        : r.hasPassword ? `locked — password required${r.owner ? ` (by ${r.owner})` : ''}`
                                        : 'click to join'}
                                >
                                    <span className="vc-room-name">{r.isLobby ? '🏠 ' : ''}{r.name}{r.hasPassword ? ' 🔒' : ''}</span>
                                    <span className="vc-room-count">{r.users}{r.publishers > 0 ? ` · ${r.publishers}🎥` : ''}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="vc-roster-head">
                        <span className="vc-roster-title">In “{currentRoom}” ({roster.length})</span>
                        {isStaff && <button className="vc-bans-btn" onClick={openBans} title="manage bans">⚖ Bans</button>}
                    </div>
                    <ul className="vc-roster">
                        {roster.map((u) => {
                            const stream = streamByUid.get(u.id);
                            const sub = stream ? watched.get(stream.stream_id) : undefined;
                            const isWatching = !!sub;
                            const isSelf = me && u.id === me.id;
                            const audioOnly = stream ? stream.meta?.hasVideo === false : false;
                            const badge = rankBadge(u.k);
                            return (
                                <li key={u.id > 0 ? `u${u.id}` : `g${u.name}`} className="vc-roster-row">
                                    <span className="vc-roster-name" style={{ color: u.color }}>{u.name}</span>
                                    {badge && <span className="vc-rank-badge" style={{ color: badge.color, borderColor: badge.color }} title={rankLabel(u.k)}>{badge.short}</span>}
                                    {u.publishing && (audioOnly
                                        ? <span className="vc-mic-on" title="sharing microphone">🎙</span>
                                        : <span className="vc-cam-on" title="sharing camera">●</span>)}
                                    {!isSelf && (
                                        <span className="vc-roster-ctrls">
                                            {!isGuest && u.id > 0 && (
                                                <button className="vc-icon-btn" title={`whisper @${u.name}`}
                                                    onClick={() => { setDraft(`@${u.name} `); setDismissed(true); inputRef.current?.focus(); }}
                                                >✉</button>
                                            )}
                                            {stream && (
                                                <>
                                                    <button
                                                        className={`vc-icon-btn ${isWatching ? '' : 'off'}`}
                                                        title={isWatching ? (audioOnly ? 'stop listening' : 'stop watching') : (audioOnly ? 'listen' : 'watch video')}
                                                        onClick={() => isWatching ? unsubscribe(stream.stream_id) : subscribe(stream)}
                                                    >{audioOnly ? '🎧' : '👁'}</button>
                                                    {isWatching && (
                                                        <button
                                                            className={`vc-icon-btn ${sub!.muted ? 'off' : ''}`}
                                                            title={sub!.muted ? 'unmute' : 'mute'}
                                                            onClick={() => toggleMute(stream.stream_id)}
                                                        >{sub!.muted ? '🔇' : '🔊'}</button>
                                                    )}
                                                    {me?.is_admin && (
                                                        recordingStreams.has(stream.stream_id)
                                                            ? <button className="vc-rec-btn vc-rec-on" onClick={() => toggleRecord(stream.stream_id, false)} title="stop recording">●</button>
                                                            : <button className="vc-rec-btn" onClick={() => toggleRecord(stream.stream_id, true)} title="record">rec</button>
                                                    )}
                                                </>
                                            )}
                                            {canModerate(u) && (
                                                <button className="vc-icon-btn vc-mod-btn" title="moderate user"
                                                    onClick={() => { setModReason(''); setModTarget(u); }}
                                                >🛡</button>
                                            )}
                                        </span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                    <div className="vc-watch-hint">Watching {activeSubs.length}/{maxSubs} — a 5th replaces the oldest.</div>
                </aside>
            </div>

            {pwModal && (
                <div className="vc-modal-backdrop" onClick={() => setPwModal(null)}>
                    <div className="vc-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="vc-modal-title">Password for “{pwModal.room}”</div>
                        <form onSubmit={(e) => { e.preventDefault(); if (pwInput) joinRoom(pwModal.room, pwInput); }}>
                            <input
                                className="vc-modal-input" type="password" autoFocus value={pwInput}
                                onChange={(e) => { setPwInput(e.target.value); setPwError(false); }}
                                placeholder="Room password" maxLength={128}
                            />
                            {pwError && <div className="vc-modal-err">Wrong password — try again.</div>}
                            <div className="vc-modal-actions">
                                <button type="button" className="vc-btn vc-btn-ghost" onClick={() => setPwModal(null)}>Cancel</button>
                                <button type="submit" className="vc-btn vc-btn-go" disabled={!pwInput}>Join</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {settingsOpen && (
                <div className="vc-modal-backdrop" onClick={() => setSettingsOpen(false)}>
                    <div className="vc-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="vc-modal-title">Mic / camera settings</div>
                        <label className="vc-set-label">Microphone</label>
                        <select className="vc-modal-input" value={audioInId} onChange={(e) => chooseAudioIn(e.target.value)}>
                            <option value="">Default microphone</option>
                            {devices.audioIn.map((d, i) => (
                                <option key={d.deviceId || i} value={d.deviceId}>{d.label || `Microphone ${i + 1}`}</option>
                            ))}
                        </select>
                        <label className="vc-set-label">Camera</label>
                        <select className="vc-modal-input" value={videoInId} onChange={(e) => chooseVideoIn(e.target.value)}>
                            <option value="">Default camera</option>
                            {devices.videoIn.map((d, i) => (
                                <option key={d.deviceId || i} value={d.deviceId}>{d.label || `Camera ${i + 1}`}</option>
                            ))}
                        </select>
                        {publishing && <div className="vc-set-hint">Stop &amp; re-share to switch devices on a live feed.</div>}
                        <div className="vc-modal-actions">
                            <button type="button" className="vc-btn vc-btn-go" onClick={() => setSettingsOpen(false)}>Done</button>
                        </div>
                    </div>
                </div>
            )}

            {newModal && (
                <div className="vc-modal-backdrop" onClick={() => setNewModal(false)}>
                    <div className="vc-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="vc-modal-title">Create a party room</div>
                        <form onSubmit={(e) => { e.preventDefault(); const n = newName.trim(); if (n) createRoom(n, newPw.trim() || undefined); }}>
                            <input
                                className="vc-modal-input" autoFocus value={newName}
                                onChange={(e) => setNewName(e.target.value.replace(/[^A-Za-z0-9_\-\/]/g, ''))}
                                placeholder="room name (letters, numbers, - _)" maxLength={64}
                            />
                            <input
                                className="vc-modal-input" type="password" value={newPw}
                                onChange={(e) => setNewPw(e.target.value)}
                                placeholder="password (optional)" maxLength={128}
                            />
                            <div className="vc-modal-actions">
                                <button type="button" className="vc-btn vc-btn-ghost" onClick={() => setNewModal(false)}>Cancel</button>
                                <button type="submit" className="vc-btn vc-btn-go" disabled={!newName.trim()}>Create</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {modTarget && (
                <div className="vc-modal-backdrop" onClick={() => setModTarget(null)}>
                    <div className="vc-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="vc-modal-title">Moderate <span style={{ color: modTarget.color }}>{modTarget.name}</span></div>
                        <input className="vc-modal-input" value={modReason} maxLength={200}
                            onChange={(e) => setModReason(e.target.value)} placeholder="reason (optional)" />
                        <div className="vc-mod-actions">
                            <button className="vc-btn vc-btn-ghost" onClick={() => { modKick(modTarget.id, modReason || undefined); setModTarget(null); }}>Kick</button>
                            {BAN_DURATIONS.map((d) => (
                                <button key={d.minutes} className="vc-btn vc-btn-ban"
                                    onClick={() => { modBan(modTarget.id, d.minutes || undefined, modReason || undefined); setModTarget(null); }}
                                >Ban · {d.label}</button>
                            ))}
                        </div>
                        <div className="vc-modal-actions">
                            <button className="vc-btn vc-btn-ghost" onClick={() => setModTarget(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {bansOpen && (
                <div className="vc-modal-backdrop" onClick={() => setBansOpen(false)}>
                    <div className="vc-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="vc-modal-title">Active bans ({banList.length})</div>
                        {banList.length === 0
                            ? <div className="vc-set-hint">No active bans.</div>
                            : <ul className="vc-ban-list">
                                {banList.map((b) => (
                                    <li key={b.uid} className="vc-ban-row">
                                        <span className="vc-ban-info">uid {b.uid}{b.until ? ` · until ${new Date(b.until * 1000).toLocaleString()}` : ' · permanent'}{b.reason ? ` · ${b.reason}` : ''} <i>by {b.by}</i></span>
                                        <button className="vc-btn vc-btn-go" onClick={() => { modUnban(b.uid); setTimeout(modListBans, 150); }}>Unban</button>
                                    </li>
                                ))}
                            </ul>}
                        <div className="vc-modal-actions">
                            <button className="vc-btn vc-btn-ghost" onClick={() => setBansOpen(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {me && disclaimerRequired && (
                <div className="vc-disc-overlay">
                    <div className="vc-disc-box">
                        <div className="vc-disc-title">{DISCLAIMER_TITLE}</div>
                        {declined ? (
                            <div className="vc-disc-declined">
                                <p>You must accept the Terms of Use &amp; Disclaimer to use FFR Video Chat.</p>
                                <button className="vc-btn vc-btn-go" onClick={() => setDeclined(false)}>Back</button>
                            </div>
                        ) : (
                            <>
                                <p className="vc-disc-intro">{DISCLAIMER_INTRO}</p>
                                <div className="vc-disc-body">
                                    {DISCLAIMER_SECTIONS.map((s) => (
                                        <div key={s.h} className="vc-disc-sec">
                                            <div className="vc-disc-h">{s.h}</div>
                                            {s.p && <p className="vc-disc-p">{s.p}</p>}
                                            {s.list && <ul className="vc-disc-list">{s.list.map((li, i) => <li key={i}>{li}</li>)}</ul>}
                                        </div>
                                    ))}
                                </div>
                                <label className="vc-disc-agree">
                                    <input type="checkbox" checked={agreeChecked} onChange={(e) => setAgreeChecked(e.target.checked)} />
                                    <span>{DISCLAIMER_AGREE_LABEL}</span>
                                </label>
                                <div className="vc-disc-actions">
                                    <button className="vc-btn vc-btn-ghost" onClick={() => setDeclined(true)}>Decline</button>
                                    <button className="vc-btn vc-btn-go" disabled={!agreeChecked} onClick={() => acceptDisclaimer()}>Agree &amp; Enter</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {banned && (
                <div className="vc-banned-overlay">
                    <div className="vc-banned-box">
                        <div className="vc-banned-title">You have been banned</div>
                        <div>{banned.until ? `Until ${new Date(banned.until * 1000).toLocaleString()}` : 'This ban is permanent.'}</div>
                        {banned.reason && <div className="vc-banned-reason">Reason: {banned.reason}</div>}
                        <div className="vc-set-hint">by {banned.by}</div>
                    </div>
                </div>
            )}
        </div>
    );
}
